/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ExpenseModelDataViewModel } from 'src/app/receipt-long-op/view-models/expense-model-data.view-model';
import { firstValueFrom, Observable } from 'rxjs';
import { ExpandableComponent, MessageResourceManager, NTSTranslatePipe } from '@nts/std';
import { NgFor, NgIf } from '@angular/common';
import { ModelBoxComponent } from 'src/app/shared/components/model-box/model-box.component';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { BaseStepComponent } from '../base-step.component';
import { FileUploadStatusInterface, MultipleFileUploadComponent } from 'src/app/shared/components/multiple-file-upload/multiple-file-upload.component';
import { FullscreenPreviewComponent } from 'src/app/shared/components/fullscreen-preview/fullscreen-preview.component';
import { ExpenseFileCollectionItemViewModel } from 'src/app/receipt-long-op/view-models/expense-file.collection-item-view-model';
import { UserOfTenantIdentity } from 'src/app/external-remote/user-of-tenant/user-of-tenant.identity';
import { BigNumber } from '@nts/std/types';
import { ExpenseFileUtils } from 'src/app/receipt-long-op/utils/expense-file.utils';

@UntilDestroy()
@Component({
  selector: 'nts-expense-model-data-step',
  templateUrl: './expense-model-data-step.component.html',
  styleUrls: ['./expense-model-data-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NTSTranslatePipe,
    NgIf,
    ModelBoxComponent,
    SvgIconComponent,
    NgFor,
    MultipleFileUploadComponent,
    FullscreenPreviewComponent,
    ExpandableComponent
  ]
})
export class ExpenseModelDataStepComponent extends BaseStepComponent<ExpenseModelDataViewModel> {

  get currentExpenseUserIdentity(): UserOfTenantIdentity {
    return this.rootViewModel.params.userRef?.getDomainModel()?.currentIdentity;
  }

  untilDestroyed(): <U>(source: Observable<U>) => Observable<U> {
    return untilDestroyed(this);
  }

  async onViewModelLoaded() {
    const isReadOnly = await this.isReadOnly(this.rootViewModel);
    this.receiptStepsService.stepLoading.next(true);
    this.receiptStepsService.nextStepEnabled.next(false);
    this.receiptStepsService.isReadOnly$.next(isReadOnly);

    this.rootViewModel.params.expenseData.files.collectionChanged.pipe(this.untilDestroyed()).subscribe(() => {
      this.nextStepEnableIfFileExists(isReadOnly);
    });

    const queryParams = await firstValueFrom(this.routeStateService.queryParams);
    const expenseModelId = parseInt(queryParams['expenseModelId'], 10);


    this.rootViewModel.params.expenseModelData.availableModels = await this.receiptStepsService.orchestratorViewModel.getAvailableModels(
      this.currentExpenseUserIdentity,
      this.rootViewModel.params?.expenseState.value
    );

    const modelList = await firstValueFrom(this.rootViewModel.params.expenseModelData.expenseModelRef.execExternalList(undefined, false));

    let expenseAnnotationId = null;
    if (queryParams['expenseAnnotationId']) {
      expenseAnnotationId = parseInt(queryParams['expenseAnnotationId'], 10);
    }
    const remoteExpense = queryParams['remoteExpense'] === 'true';

    await this.rootViewModel.params.expenseModelData.initPresentationLogic(
      expenseAnnotationId,
      remoteExpense,
      modelList,
      expenseModelId,
      this.rootViewModel.params.expenseState,
      !isReadOnly
    );

    this.inIframe = this.activatedRoute.snapshot.queryParams['iframe'] === 'true' || window.self !== window.top;


    if (modelList && modelList.length == 1) {
      if (this.rootViewModel.params.expenseModelData.expenseModelRef.modelCode.value == null) {
        await this.selectModel(modelList[0]);
      }
      this.rootViewModel.params.expenseModelData.existOnlyOneModel = true;
    }

    this.receiptStepsService.stepLoading.next(false);

    const title = MessageResourceManager.Current.getMessage("ExpenseModelDataStep_Title");
    this.telemetryService.trackPageView({
      name: title,
      properties: {
        selectedModelCode: this.rootViewModel.params.expenseModelData?.expenseModelRef?.modelCode?.value
      }
    });

    this.nextStepEnableIfFileExists(isReadOnly);
    this.cd.detectChanges();
  };

  nextStepEnableIfFileExists(isReadOnly: boolean): void {
    if (this.rootViewModel.params.expenseData?.files?.length > 0 || isReadOnly) {
      this.receiptStepsService.nextStepEnabled.next(true);
    } else {
      this.receiptStepsService.nextStepEnabled.next(false);
    }
  }

  async selectModel(model: {
    description: string,
    identity: { [key: string]: string | number | BigNumber; },
    all: { [key: string]: string | number | BigNumber; }[];
  }) {
    this.receiptStepsService.stepLoading.next(true);
    await this.rootViewModel.params.expenseModelData.expenseModelRef.setPlainPascalCaseIdentity(model.identity);
    this.receiptStepsService.stepLoading.next(false);
    this.telemetryService.trackEvent({
      name: 'WEA_ReceiptLongOp_ExpenseModelDataStep_SelectModelCode',
      properties: {
        modelCode: this.rootViewModel.params.expenseModelData?.expenseModelRef?.modelCode?.value
      }
    });
  }

  fileUploadValidation() {
    this.rootViewModel.params.expenseData.validateStep().then(() => {
      this.cd.detectChanges();
    }).catch(() => { });
  }

  async addPrimaryFile(): Promise<ExpenseFileCollectionItemViewModel> {
    const item = await this.rootViewModel.params.expenseData.files.addNewItem();
    item.isPrimaryFile.value = true;
    return item;
  }

  isPrimarySectionFile(item: FileUploadStatusInterface): boolean {
    return (item as ExpenseFileCollectionItemViewModel).isPrimaryFile.value === true;
  }

  viewExpenseFile(item: ExpenseFileCollectionItemViewModel): void {
    ExpenseFileUtils.viewExpenseFile(
      item,
      this.rootViewModel.params.expenseData,
      this.receiptStepsService.orchestratorViewModel,
      this.sanitizer,
      () => this.cd.detectChanges()
    );
  }
}

